import React, { useState, useEffect } from "react";
import {
    Box,
    TextField,
    Button,
    Paper,
    Typography,
    IconButton,
    Tooltip,
    Alert,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    List,
    ListItem,
    ListItemText,
    ListItemSecondaryAction,
    CircularProgress,
} from "@mui/material";
import {
    ContentCopy as ContentCopyIcon,
    Refresh as RefreshIcon,
    Save as SaveIcon,
    History as HistoryIcon,
    Delete as DeleteIcon,
} from "@mui/icons-material";
import { generateRandomSalt } from "../../utils/saltGenerator";
import saltStorageService, { StoredSalt } from "../../services/saltStorageService";
import { useContracts } from "../context/ContractContext";
import logger from "../../utils/logger";

interface SaltGeneratorProps {
    contractAddress?: string;
    functionName?: string;
    onSaltGenerated?: (salt: string, id?: string) => void;
}

/**
 * Enhanced salt generator with backend storage capabilities
 * Generates random salt values for timelockController.schedule and execute functions.
 * The salt guarantees uniqueness of the OperationID which must be identical for execute to work based on a schedule() operationID.
 */
const SaltGenerator: React.FC<SaltGeneratorProps> = ({
    contractAddress,
    functionName,
    onSaltGenerated
}) => {
    const [salt, setSalt] = useState<string>(generateRandomSalt());
    const [copied, setCopied] = useState<boolean>(false);
    const [isGenerating, setIsGenerating] = useState<boolean>(false);
    const [isSaving, setIsSaving] = useState<boolean>(false);
    const [savedSaltId, setSavedSaltId] = useState<string | null>(null);
    const [showHistory, setShowHistory] = useState<boolean>(false);
    const [storedSalts, setStoredSalts] = useState<StoredSalt[]>([]);
    const [isLoadingHistory, setIsLoadingHistory] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);

    // Get contracts from the existing infrastructure
    const { contracts } = useContracts();

    // Load stored salts when component mounts
    useEffect(() => {
        loadStoredSalts();
    }, []);

    // Load stored salts from backend
    const loadStoredSalts = async () => {
        try {
            setIsLoadingHistory(true);
            setError(null);
            const salts = await saltStorageService.listSalts();
            setStoredSalts(salts);
        } catch (error) {
            logger.error('Error loading stored salts:', error);

            // Handle specific Auth0 authentication errors
            const errorMessage = error instanceof Error ? error.message : 'Unknown error';
            if (errorMessage.includes('Authentication required') || errorMessage.includes('session may have expired')) {
                setError('Authentication required. Please refresh the page and log in again to access stored salts.');
            } else if (errorMessage.includes('Access forbidden') || errorMessage.includes('insufficient permissions')) {
                setError('Access denied. Your account does not have permission to access stored salts.');
            } else if (errorMessage.includes('Server error') || errorMessage.includes('temporarily unavailable')) {
                setError('Backend service is temporarily unavailable. Please try again later.');
            } else {
                setError(`Failed to load stored salts: ${errorMessage}`);
            }
        } finally {
            setIsLoadingHistory(false);
        }
    };

    // Generate a new random salt using backend service with timeout and better error handling
    const handleGenerateNewSalt = async () => {
        try {
            setIsGenerating(true);
            setError(null);

            // Create a timeout promise
            const timeoutPromise = new Promise((_, reject) => {
                setTimeout(() => reject(new Error('Salt generation timed out after 10 seconds')), 10000);
            });

            // Race between salt generation and timeout
            const { salt: newSalt } = await Promise.race([
                saltStorageService.generateSalt(),
                timeoutPromise
            ]) as { salt: string };

            setSalt(newSalt);
            setSavedSaltId(null); // Reset saved state
            setCopied(false);

            // Notify parent component
            if (onSaltGenerated) {
                onSaltGenerated(newSalt);
            }

            logger.log('New salt generated via backend:', newSalt);
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error';
            logger.error('Backend salt generation failed:', errorMessage);

            // Show specific error message
            if (errorMessage.includes('timeout')) {
                setError('Backend salt generation timed out. Using local fallback.');
            } else if (errorMessage.includes('fetch')) {
                setError('Cannot connect to backend API. Using local fallback.');
            } else {
                setError(`Backend error: ${errorMessage}. Using local fallback.`);
            }

            // Fallback to local generation
            const fallbackSalt = generateRandomSalt();
            setSalt(fallbackSalt);
            setSavedSaltId(null);
            setCopied(false);

            if (onSaltGenerated) {
                onSaltGenerated(fallbackSalt);
            }

            logger.log('Using local fallback salt:', fallbackSalt);
        } finally {
            setIsGenerating(false);
        }
    };

    // Save current salt to backend
    const handleSaveSalt = async () => {
        try {
            setIsSaving(true);
            setError(null);

            // Create descriptive name using the preferred format: Salt_2025_08_12_<time>
            const now = new Date();
            const dateStr = now.toISOString().slice(0, 10).replace(/-/g, '_'); // 2025_08_12
            const timeStr = now.toTimeString().slice(0, 8).replace(/:/g, '_'); // 14_30_45
            const descriptiveName = `Salt_${dateStr}_${timeStr}`;

            const { id } = await saltStorageService.generateAndStoreSalt(
                descriptiveName,
                contractAddress,
                functionName
            );

            setSavedSaltId(descriptiveName); // Store the descriptive name instead of just the ID
            await loadStoredSalts(); // Refresh the list

            logger.log('Salt saved with descriptive ID:', descriptiveName);
        } catch (error) {
            logger.error('Error saving salt:', error);
            setError('Failed to save salt to backend');
        } finally {
            setIsSaving(false);
        }
    };

    // Extract contract name from address using the existing contracts infrastructure
    const extractContractName = (address?: string): string => {
        if (!address) return 'unknownContract';

        // Find the contract in the loaded contracts array
        const contract = contracts.find(c =>
            c.address?.toLowerCase() === address.toLowerCase()
        );

        if (contract?.name) {
            return contract.name;
        }

        // Fallback: try to derive from ABI name if available
        if (contract?.abi && Array.isArray(contract.abi)) {
            // Look for constructor or contract name in ABI metadata
            const constructorItem = contract.abi.find((item: any) => item.type === 'constructor');
            if (constructorItem?.name) {
                return constructorItem.name;
            }
        }

        // Final fallback: use ABI name + last 4 characters of address as you suggested
        const lastFour = address.slice(-4);

        // Try to get ABI name from contract if available
        if (contract?.abi) {
            // Look for common patterns in ABI to determine contract type
            const functions = contract.abi.filter((item: any) => item.type === 'function');
            const functionNames = functions.map((f: any) => f.name);

            if (functionNames.includes('getOwners') && functionNames.includes('execTransaction')) {
                return `MultiSigWallet_${lastFour}`;
            } else if (functionNames.includes('schedule') && functionNames.includes('execute')) {
                return `TimelockController_${lastFour}`;
            } else if (functionNames.includes('transfer') && functionNames.includes('balanceOf')) {
                return `ERC20Token_${lastFour}`;
            }
        }

        return `contract_${lastFour}`;
    };

    // Copy the current salt to clipboard
    const handleCopySalt = () => {
        navigator.clipboard.writeText(salt);
        setCopied(true);

        // Reset the copied state after 2 seconds
        setTimeout(() => {
            setCopied(false);
        }, 2000);
    };

    // Load a stored salt
    const handleLoadStoredSalt = async (storedSalt: StoredSalt) => {
        try {
            const saltData = await saltStorageService.retrieveSalt(storedSalt.id);
            setSalt(saltData.salt);
            setSavedSaltId(storedSalt.id);
            setCopied(false);
            setShowHistory(false);

            if (onSaltGenerated) {
                onSaltGenerated(saltData.salt, storedSalt.id);
            }

            logger.log('Loaded stored salt:', storedSalt.id);
        } catch (error) {
            logger.error('Error loading stored salt:', error);
            setError('Failed to load stored salt');
        }
    };

    // Delete a stored salt
    const handleDeleteStoredSalt = async (id: string) => {
        try {
            await saltStorageService.deleteSalt(id);
            await loadStoredSalts(); // Refresh the list

            // If the currently loaded salt was deleted, clear the saved state
            if (savedSaltId === id) {
                setSavedSaltId(null);
            }

            logger.log('Deleted stored salt:', id);
        } catch (error) {
            logger.error('Error deleting stored salt:', error);
            setError('Failed to delete stored salt');
        }
    };

    // Format date for display
    const formatDate = (timestamp: number) => {
        return new Date(timestamp).toLocaleString();
    };

    return (
        <>
            <Paper elevation={3} sx={{ p: 2, borderRadius: 1, height: '100%' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                    <Typography variant="h6">
                        Salt Generator
                    </Typography>
                    <Box>
                        {savedSaltId && (
                            <Chip
                                label="Saved"
                                color="success"
                                size="small"
                                sx={{ mr: 1 }}
                            />
                        )}
                        <Tooltip title="View Salt History">
                            <IconButton
                                onClick={() => setShowHistory(true)}
                                size="small"
                            >
                                <HistoryIcon />
                            </IconButton>
                        </Tooltip>
                    </Box>
                </Box>

                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Generate a random 64-byte hexadecimal string for use as the salt parameter in
                    timelockController.schedule and execute functions. The salt guarantees uniqueness
                    of the OperationID which must be identical for execute to work based on a schedule() operationID.
                </Typography>

                {error && (
                    <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setError(null)}>
                        {error}
                    </Alert>
                )}

                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <TextField
                        fullWidth
                        label="Generated Salt"
                        value={salt}
                        InputProps={{
                            readOnly: true,
                        }}
                    />

                    <Tooltip title="Copy to Clipboard">
                        <IconButton
                            onClick={handleCopySalt}
                            sx={{ ml: 1 }}
                            color={copied ? "success" : "default"}
                        >
                            <ContentCopyIcon />
                        </IconButton>
                    </Tooltip>

                    <Tooltip title="Save Salt">
                        <IconButton
                            onClick={handleSaveSalt}
                            sx={{ ml: 1 }}
                            disabled={isSaving || !!savedSaltId}
                        >
                            {isSaving ? <CircularProgress size={20} /> : <SaveIcon />}
                        </IconButton>
                    </Tooltip>
                </Box>

                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                        variant="contained"
                        onClick={handleGenerateNewSalt}
                        startIcon={isGenerating ? <CircularProgress size={16} /> : <RefreshIcon />}
                        disabled={isGenerating}
                        sx={{ flex: 1 }}
                    >
                        {isGenerating ? 'Generating...' : 'Generate New Salt'}
                    </Button>

                    <Button
                        variant="outlined"
                        onClick={handleSaveSalt}
                        startIcon={isSaving ? <CircularProgress size={16} /> : <SaveIcon />}
                        disabled={isSaving || !!savedSaltId}
                    >
                        Save
                    </Button>
                </Box>

                {copied && (
                    <Typography
                        variant="caption"
                        color="success.main"
                        sx={{ display: 'block', mt: 1, textAlign: 'center' }}
                    >
                        Salt copied to clipboard!
                    </Typography>
                )}

                {savedSaltId && (
                    <Typography
                        variant="caption"
                        color="success.main"
                        sx={{ display: 'block', mt: 1, textAlign: 'center' }}
                    >
                        Salt saved to secure backend storage as {savedSaltId}
                    </Typography>
                )}
            </Paper>

            {/* Salt History Dialog */}
            <Dialog
                open={showHistory}
                onClose={() => setShowHistory(false)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Typography variant="h6">Salt History</Typography>
                        <IconButton onClick={() => setShowHistory(false)}>
                            <DeleteIcon />
                        </IconButton>
                    </Box>
                </DialogTitle>
                <DialogContent>
                    {isLoadingHistory ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                            <CircularProgress />
                        </Box>
                    ) : storedSalts.length === 0 ? (
                        <Typography color="text.secondary" sx={{ textAlign: 'center', p: 3 }}>
                            No stored salts found
                        </Typography>
                    ) : (
                        <List>
                            {storedSalts.map((storedSalt) => (
                                <ListItem key={storedSalt.id} divider>
                                    <ListItemText
                                        primary={
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                                    {storedSalt.id.substring(0, 8)}...
                                                </Typography>
                                                {savedSaltId === storedSalt.id && (
                                                    <Chip label="Current" color="primary" size="small" />
                                                )}
                                            </Box>
                                        }
                                        secondary={
                                            <Box>
                                                <Typography variant="caption" color="text.secondary">
                                                    Created: {formatDate(storedSalt.created)}
                                                </Typography>
                                                <br />
                                                <Typography variant="caption" color="text.secondary">
                                                    Last accessed: {formatDate(storedSalt.lastAccessed)}
                                                </Typography>
                                            </Box>
                                        }
                                    />
                                    <ListItemSecondaryAction>
                                        <Box sx={{ display: 'flex', gap: 1 }}>
                                            <Tooltip title="Load Salt">
                                                <IconButton
                                                    onClick={() => handleLoadStoredSalt(storedSalt)}
                                                    size="small"
                                                >
                                                    <RefreshIcon />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Delete Salt">
                                                <IconButton
                                                    onClick={() => handleDeleteStoredSalt(storedSalt.id)}
                                                    size="small"
                                                    color="error"
                                                >
                                                    <DeleteIcon />
                                                </IconButton>
                                            </Tooltip>
                                        </Box>
                                    </ListItemSecondaryAction>
                                </ListItem>
                            ))}
                        </List>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShowHistory(false)}>Close</Button>
                    <Button onClick={loadStoredSalts} disabled={isLoadingHistory}>
                        Refresh
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default SaltGenerator;
